import React from 'react';
import { ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { useI18n } from '../../hooks/useI18n';
import { PackagePricingLink } from '../projects/ProductCreateBanner';
import { readyAppPackageLabelKey } from './readyAppDisplay';

export const ReadyAppDetailsModal = ({ open, app, canDeploy, onClose, onDeploy }) => {
  const { t, language } = useI18n();
  const title = language === 'fa' ? app?.titleFa : app?.titleEn;
  const description = language === 'fa' ? app?.descriptionFa : app?.descriptionEn;
  const planLabel = t(readyAppPackageLabelKey(app?.requiredPackageSlug));

  if (!open || !app) {
    return null;
  }

  return (
    <Modal open={open} onClose={onClose} title={title} panelClassName="max-w-2xl">
      <div className="space-y-5 px-2 pb-3">
        <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-950 dark:border-neutral-800">
          <img src={app.screenshotUrl} alt="" className="h-48 w-full object-cover sm:h-56" />
        </div>
        <div className="flex items-start gap-3">
          <img src={app.iconUrl} alt="" className="h-12 w-12 rounded-2xl" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-neutral-950 dark:text-white">{title}</h3>
              <span className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700 dark:bg-violet-400/15 dark:text-violet-200">
                {t('readyAppsNeedsPlan', { plan: planLabel })}
              </span>
            </div>
            <p className="mt-2 text-sm leading-6 text-neutral-500">{description}</p>
          </div>
        </div>
        <dl className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl bg-neutral-50 px-3.5 py-3 dark:bg-neutral-900">
            <dt className="text-xs text-neutral-500">{t('readyAppsCpuLabel')}</dt>
            <dd className="mt-1 text-sm font-semibold">{t('readyAppsCpuHint', { value: app.defaultCpu })}</dd>
          </div>
          <div className="rounded-xl bg-neutral-50 px-3.5 py-3 dark:bg-neutral-900">
            <dt className="text-xs text-neutral-500">{t('readyAppsRamLabel')}</dt>
            <dd className="mt-1 text-sm font-semibold">{t('readyAppsRamHint', { value: app.defaultMemoryMb })}</dd>
          </div>
        </dl>
        <div>
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">{t('readyAppsVolumesTitle')}</p>
          {app.volumes?.length ? (
            <ul className="mt-2 space-y-1.5 text-sm text-neutral-500">
              {app.volumes.map((volume) => (
                <li key={volume.name}>
                  <span dir="ltr" className="font-mono text-neutral-800 dark:text-neutral-100">{volume.name}</span>
                  {' — '}
                  {language === 'fa' ? volume.purposeFa : volume.purposeEn}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-neutral-500">{t('readyAppsVolumesEmpty')}</p>
          )}
        </div>
        {app.vendorUrl ? (
          <a
            href={app.vendorUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium text-neutral-800 hover:underline dark:text-neutral-100"
          >
            <ExternalLink size={15} />
            {t('readyAppsVendorSite')}
            <span dir="ltr">({app.vendorName || app.vendorUrl})</span>
          </a>
        ) : null}
        {!canDeploy ? (
          <p className="rounded-xl bg-amber-50 px-3.5 py-3 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-100">
            {t('readyAppsLockedHint', { plan: planLabel })}{' '}
            <PackagePricingLink>{t('pricingPurchaseTitle')}</PackagePricingLink>
          </p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button type="button" disabled={!canDeploy} onClick={onDeploy}>
            {t('readyAppsDeploy')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
